import { createFileRoute } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { BlueprintGrid, BpFrame, CoordLabel } from "@/components/blueprint/Blueprint";
import { SEOHead } from "@/components/shared/SEOHead";
import { logoUrl } from "@/lib/assets";

export const Route = createFileRoute("/raise")({
  component: RaisePage,
  validateSearch: (search: Record<string, unknown>) => ({
    key: typeof search.key === "string" ? (search.key as string) : undefined,
    print: typeof search.print === "string" ? (search.print as string) : undefined,
  }),
});

/** `[CONFIRM]` placeholder — mustard pill on screen, plain text in print. */
function Confirm({ children }: { children: ReactNode }) {
  return <span className="bp-confirm">{children}</span>;
}

const TOTAL_SLIDES = 6;
const PRODUCT_OVERVIEW =
  "Quuil is a mobile design and merchandise platform that lets anyone create custom artwork, preview it on real products, order it, track it, and share it from their phone.";

const STORY_SCREENS = [
  {
    title: "Start",
    src: "/press-assets/screenshots/Main Home Screen - light.PNG",
  },
  {
    title: "Create",
    src: "/press-assets/screenshots/describe your idea screen - dark.PNG",
  },
  {
    title: "Edit",
    src: "/press-assets/screenshots/image editor - add text - light.PNG",
  },
  {
    title: "Preview",
    src: "/press-assets/screenshots/Real mockup preview - dark screen.PNG",
  },
  {
    title: "Cart",
    src: "/press-assets/screenshots/cart. screen - dark.PNG",
  },
];

function SlideFooter({ slide }: { slide: number }) {
  return (
    <div className="bp-raise-slide-footer">
      <span>QUL-INV-002</span>
      <span>
        Slide {slide} of {TOTAL_SLIDES}
      </span>
    </div>
  );
}

function RaisePage() {
  const { key, print } = Route.useSearch();
  const isLight = print === "light";
  const hasKey = Boolean(key);

  useEffect(() => {
    document.body.classList.add("is-printing-raise");
    return () => {
      document.body.classList.remove("is-printing-raise");
    };
  }, []);

  if (!hasKey) {
    return (
      <div className={`bp-raise-page ${isLight ? "is-light" : ""}`}>
        <SEOHead
          title="Quuil — Investor Pack"
          description="Quuil — investor pack. Access by invitation."
          path="/raise"
          robots="noindex,nofollow"
        />
        <div className="bp-raise-gate">
          <div>
            <span className="bp-kicker" style={{ justifyContent: "center" }}>
              Quuil // Investor Pack
            </span>
            <h1 className="bp-press-headline" style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)" }}>
              By Invitation Only.
            </h1>
            <p>
              This page is for invited investors. Request access at{" "}
              <a href="mailto:hello@quuil.com">hello@quuil.com</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bp-raise-page ${isLight ? "is-light" : ""}`}>
      <SEOHead
        title="Quuil — Investor Pack"
        description="Quuil — mobile design, real merchandise, and community discovery. Pre-seed angel round."
        path="/raise"
        robots="noindex,nofollow"
      />

      <main>
        {/* ============================================================
            SECTION 0 — INVESTOR ONE-PAGER
        ============================================================ */}
        <section className="bp-raise-slide bp-raise-onepager" aria-label="Investor one-pager">
          <BlueprintGrid light />

          <div className="bp-hero-border" aria-hidden="true">
            <div className="bp-title-block">
              <span>Project</span>
              <strong>Quuil — Investor One-Pager</strong>
              <small>DWG NO. QUL-INV-001 // SCALE 1:1</small>
            </div>
            <CoordLabel className="bp-border-a1">A1</CoordLabel>
            <CoordLabel className="bp-border-a8">A8</CoordLabel>
            <CoordLabel className="bp-border-h1">H1</CoordLabel>
            <CoordLabel className="bp-border-h8">H8</CoordLabel>
          </div>

          <div className="bp-raise-slide-inner">
            <header>
              <span className="bp-kicker">Quuil // Mobile Design. Real Products.</span>
              <p
                className="bp-subtitle"
                style={{
                  margin: 0,
                  fontFamily: "var(--bp-display)",
                  fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                  color: "var(--bp-white)",
                  lineHeight: 1,
                }}
              >
                Create. Customize. Order. Share.
              </p>
            </header>

            <BpFrame label="What It Is">
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.6 }}>
                {PRODUCT_OVERVIEW} Users can start from a photo, sketch, prompt,
                blank draft, or community design, then move through mockup editing,
                cart, checkout, order tracking, and reordering without leaving the app.
              </p>
            </BpFrame>

            <BpFrame label="Why Now">
              <ul style={{ padding: "1.25rem 1.5rem 1.25rem 2.5rem", margin: 0, lineHeight: 1.65 }}>
                <li>Mobile creation is now the default creative surface.</li>
                <li>
                  On-demand fulfillment networks finally support quantity-of-one
                  product creation at consumer speed.
                </li>
                <li>
                  AI-assisted generation makes starting easier, but users still need
                  editing, preview, checkout, tracking, and a place to share.
                </li>
              </ul>
            </BpFrame>

            <BpFrame label="Where We Are">
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.6 }}>
                iOS shipped 2026 by two cofounders with no outside capital. The
                product includes design tools, mockup editor, Stripe checkout,
                order history, reorder flows, push updates, credits, and The
                Inkwell community. B2B route is in production at quuil.com/business.{" "}
                <Confirm>[CONFIRM downloads]</Confirm> downloads since launch,{" "}
                <Confirm>[CONFIRM designs]</Confirm> designs created in The Inkwell,
                founded to shipped in <Confirm>[CONFIRM days]</Confirm> days. The
                next 90 days are about converting shipped product capability into
                repeat purchase and community supply.
              </p>
            </BpFrame>

            <BpFrame label="The Moat">
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.6 }}>
                The Inkwell plus the product graph. Every saved design, edited
                mockup, purchased product, creator profile, and reorder creates
                context the next user can browse, remix, or buy from. The catalog
                is not static inventory; it compounds through use.
              </p>
            </BpFrame>

            <BpFrame label="Business Model">
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.6 }}>
                Consumer: credit packs for design processing and per-product
                margin on merchandise. Teams: tiered B2B pricing for organizations
                and events. Creator rewards are the community-side expansion path.
              </p>
            </BpFrame>

            <BpFrame label="Team">
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.6 }}>
                Nel Toms (Cofounder — brand, product, iOS) and Jake Biller
                (Cofounder — engineering, web, backend). Cofounded 2024,
                self-funded build, shipped 2026. Bios pending. Two operators who
                built and shipped what most teams need three more hires to attempt.
              </p>
            </BpFrame>

            <BpFrame label="Raising">
              <p style={{ padding: "1.25rem", margin: 0, lineHeight: 1.6 }}>
                Pre-seed angel round. $500K target ($350K floor / $750K ceiling).
                Post-money SAFE, $5M–$8M cap <Confirm>[CONFIRM]</Confirm>.
                The cash is for two things: build Android, and learn how to sell
                Quuil with focus. We will test a small set of creator, social,
                and community campaigns, then put more behind what actually turns
                people into repeat customers. Lead profile: one operator-angel
                anchor at $100K–$150K + roll-up of 8–15 angels at $25K–$50K.
              </p>
            </BpFrame>

            <div style={{ marginTop: "1rem" }}>
              <span className="bp-raise-cta-dot">
                <a href="mailto:nel@quuil.com">nel@quuil.com</a>
                {"  //  "}
                <a href="mailto:jake@quuil.com">jake@quuil.com</a>
                {"  //  "}
                <Confirm>[CONFIRM calendly]</Confirm>
              </span>
            </div>
          </div>
        </section>

        {/* ============================================================
            SLIDE 1 — COVER
        ============================================================ */}
        <section className="bp-raise-slide" aria-label="Slide 1 — Cover">
          <BlueprintGrid />
          <div className="bp-raise-slide-inner" style={{ textAlign: "center", placeItems: "center" }}>
            <img
              src={logoUrl}
              alt="Quuil"
              width={760}
              height={220}
              className="bp-hero-logo"
              style={{ margin: "0 auto" }}
            />
            <span className="bp-kicker" style={{ justifyContent: "center" }}>
              Mobile Design // Real Merchandise
            </span>
            <p
              style={{
                margin: 0,
                fontFamily: "var(--bp-display)",
                fontSize: "clamp(2rem, 5vw, 3.6rem)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: "var(--bp-white)",
                lineHeight: 1,
              }}
            >
              Create. Customize. Order. Share.
            </p>
          </div>
          <div className="bp-raise-slide-footer">
            <span>Send pack — Summit Madrid 2026</span>
            <span>QUL-INV-002</span>
          </div>
        </section>

        {/* ============================================================
            SLIDE 2 — THE INSIGHT
        ============================================================ */}
        <section className="bp-raise-slide" aria-label="Slide 2 — The insight">
          <BlueprintGrid light />
          <div className="bp-raise-slide-inner">
            <span className="bp-kicker">The Core Loop</span>
            <h2 className="bp-raise-display">
              Start anywhere. <span>Finish with a product.</span>
            </h2>
            <p className="bp-subtitle" style={{ margin: 0, maxWidth: 820 }}>
              {PRODUCT_OVERVIEW} The loop is simple: create, customize, order,
              share. A photo, sketch, prompt, or Inkwell design can become a tee,
              hoodie, mug, notebook, or sticker without forcing the user into a
              desktop workflow.
            </p>
            <div className="bp-raise-phone-strip" aria-label="Quuil core loop screenshots">
              {STORY_SCREENS.slice(0, 5).map((screen) => (
                <figure key={screen.title}>
                  <img src={screen.src} alt={`Quuil ${screen.title} screen`} loading="lazy" />
                  <figcaption>{screen.title}</figcaption>
                </figure>
              ))}
            </div>
            <div className="bp-raise-counter">
              Counter: print-on-demand has existed for years.
              <br />
              Counter to that: most tools still start at the upload and end at
              the file. Quuil owns the full mobile path from idea to product to
              community supply.
            </div>
          </div>
          <SlideFooter slide={2} />
        </section>

        {/* ============================================================
            SLIDE 3 — PRODUCT
        ============================================================ */}
        <section className="bp-raise-slide" aria-label="Slide 3 — Product">
          <BlueprintGrid />
          <div className="bp-raise-slide-inner">
            <span className="bp-kicker">Product</span>
            <h2 className="bp-raise-display">
              Create it. Place it. <span>Buy it.</span>
            </h2>
            <BpFrame label="LIVE PREVIEW // STAGING.QUUIL.COM">
              <div className="bp-raise-product-visual">
                <figure>
                  <img
                    src="/press-assets/screenshots/product catalogue - dark.PNG"
                    alt="Quuil product catalog screen"
                    loading="lazy"
                  />
                  <figcaption>Choose the product</figcaption>
                </figure>
                <figure>
                  <img
                    src="/press-assets/screenshots/Choose a color screen - dark.PNG"
                    alt="Quuil product color selection screen"
                    loading="lazy"
                  />
                  <figcaption>Pick color and variant</figcaption>
                </figure>
                <figure>
                  <img
                    src="/press-assets/screenshots/Real mockup preview - dark screen.PNG"
                    alt="Quuil real mockup preview screen"
                    loading="lazy"
                  />
                  <figcaption>Preview before checkout</figcaption>
                </figure>
              </div>
            </BpFrame>
            <p className="bp-subtitle" style={{ margin: 0 }}>
              Background removal, style effects, crop, rotate, scale, product
              variants, print-area detection, live previews, Stripe checkout,
              order tracking, and reordering are all part of the same flow.
            </p>
          </div>
          <SlideFooter slide={3} />
        </section>

        {/* ============================================================
            SLIDE 4 — TRACTION + THE MOAT
        ============================================================ */}
        <section className="bp-raise-slide" aria-label="Slide 4 — Where we are">
          <BlueprintGrid light />
          <div className="bp-raise-slide-inner">
            <span className="bp-kicker">Where We Are // The Moat</span>
            <p
              style={{
                margin: 0,
                color: "var(--bp-dim)",
                fontFamily: "var(--bp-mono)",
                fontSize: "0.8rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Built. Live. Learning from real design, cart, and order behavior.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)",
                gap: "1.5rem",
              }}
              className="bp-raise-traction-grid"
            >
              <div style={{ display: "grid", gap: "0.6rem" }}>
                <BpFrame label="Design Tools" className="bp-raise-stat">
                  <strong>
                    Live
                  </strong>
                  <span>Photo upload, AI assist, editing, variations, saved history</span>
                </BpFrame>
                <BpFrame label="Inkwell" className="bp-raise-stat">
                  <strong>
                    Live
                  </strong>
                  <span>Trending, new, saved designs, creator profiles, reuse</span>
                </BpFrame>
                <BpFrame label="Shopping" className="bp-raise-stat">
                  <strong>
                    Live
                  </strong>
                  <span>Cart, coupons, Stripe checkout, order history, tracking</span>
                </BpFrame>
              </div>
              <div className="bp-raise-counter" style={{ alignSelf: "center" }}>
                Two technical cofounders, no outside capital, full stack shipped:
                iOS, web, backend, payments, order fulfillment, B2B route, and
                community gallery. The moat starts with Inkwell supply and deepens
                as designs become mockups, products, orders, reorders, and creator
                portfolios.
              </div>
            </div>
            <BpFrame label="THE INKWELL // COMMUNITY SUPPLY">
              <div className="bp-raise-landscape-pair">
                <figure>
                  <img
                    src="/press-assets/screenshots/art gallery - landscape -dark.PNG"
                    alt="Quuil Inkwell art gallery landscape screen"
                    loading="lazy"
                  />
                  <figcaption>Browse and save community work</figcaption>
                </figure>
                <figure>
                  <img
                    src="/press-assets/screenshots/pick a design - landscape -dark.PNG"
                    alt="Quuil pick a design landscape screen"
                    loading="lazy"
                  />
                  <figcaption>Use a design as a new starting point</figcaption>
                </figure>
              </div>
            </BpFrame>
          </div>
          <SlideFooter slide={4} />
        </section>

        {/* ============================================================
            SLIDE 4.5 — BUILT BY TWO
        ============================================================ */}
        <section className="bp-raise-slide" aria-label="Slide 4.5 — Built by two">
          <BlueprintGrid />
          <div className="bp-raise-slide-inner">
            <span className="bp-kicker">Team</span>
            <h2 className="bp-raise-display">
              Built by <span>two.</span>
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gap: "1.25rem",
              }}
              className="bp-raise-team-grid"
            >
              <BpFrame label="Nel Toms — Cofounder">
                <div style={{ padding: "1.25rem", display: "grid", gap: "0.75rem" }}>
                  <strong style={{ fontFamily: "var(--bp-display)", fontSize: "1.6rem", letterSpacing: "0.04em" }}>
                    Brand. Product. iOS.
                  </strong>
                  <p style={{ margin: 0, color: "var(--bp-dim)", lineHeight: 1.55 }}>
                    [bio pending]
                  </p>
                  <a
                    href="https://linkedin.com/in/nel-t-2586bb7"
                    style={{
                      color: "var(--bp-yellow)",
                      fontFamily: "var(--bp-mono)",
                      fontSize: "0.74rem",
                      letterSpacing: "0.08em",
                    }}
                  >
                    linkedin.com/in/nel-t-2586bb7
                  </a>
                </div>
              </BpFrame>
              <BpFrame label="Jake Biller — Cofounder">
                <div style={{ padding: "1.25rem", display: "grid", gap: "0.75rem" }}>
                  <strong style={{ fontFamily: "var(--bp-display)", fontSize: "1.6rem", letterSpacing: "0.04em" }}>
                    Engineering. Web. Backend.
                  </strong>
                  <p style={{ margin: 0, color: "var(--bp-dim)", lineHeight: 1.55 }}>
                    [bio pending]
                  </p>
                  <a
                    href="https://linkedin.com/in/jacobbiller"
                    style={{
                      color: "var(--bp-yellow)",
                      fontFamily: "var(--bp-mono)",
                      fontSize: "0.74rem",
                      letterSpacing: "0.08em",
                    }}
                  >
                    linkedin.com/in/jacobbiller
                  </a>
                </div>
              </BpFrame>
            </div>
            <p
              style={{
                margin: 0,
                color: "var(--bp-dim)",
                fontFamily: "var(--bp-mono)",
                fontSize: "0.78rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                lineHeight: 1.7,
              }}
            >
              Cofounded 2024. Self-funded. Shipped 2026. No outside capital. Now
              raising for the next 18 months.
            </p>
          </div>
          <SlideFooter slide={5} />
        </section>

        {/* ============================================================
            SLIDE 5 — THE ASK
        ============================================================ */}
        <section className="bp-raise-slide" aria-label="Slide 5 — The ask">
          <BlueprintGrid light />
          <div className="bp-raise-slide-inner">
            <span className="bp-kicker">The Ask</span>
            <h2 className="bp-raise-display">
              Raising $500K // <span>Pre-seed angel.</span>
            </h2>
            <p className="bp-subtitle" style={{ margin: 0, maxWidth: 820 }}>
              This round gets Quuil onto Android and gives us enough marketing
              budget to find the channels that work. We are not raising for vague
              brand spend. We are raising to test clear campaigns, measure what
              brings people in, and double down on the paths that lead to orders
              and repeat use.
            </p>
            <div>
              <span className="bp-raise-cta-dot">
                Reply to this thread or grab time:{" "}
                <Confirm>[CONFIRM calendly]</Confirm>
              </span>
            </div>
            <p
              style={{
                margin: 0,
                color: "var(--bp-dim)",
                fontFamily: "var(--bp-mono)",
                fontSize: "0.74rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Met at Summit Madrid // <Confirm>[date]</Confirm>
            </p>
          </div>
          <SlideFooter slide={6} />
        </section>
      </main>
    </div>
  );
}
